import React, { FC } from 'react';

import { Typography } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import CloseIcon from '../assets/icons/CloseIcon';
import CustomButton from '../components/UI/CustomButton/CustomButton';
import {
  colorsPalette,
  MIDDLE_MARGIN_PX,
  SMALL_MARGIN,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
} from '../theme';

interface IConfirmModal {
  closeModal: () => void;
  onSubmit: () => void;
  isOpen: boolean;
  title: string;
  description?: string;
  cancelBtnCaption?: string;
  submitBtnCaption?: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cancelBtn: {
      marginBottom: SMALL_MARGIN,
      [theme.breakpoints.up('sm')]: {
        marginRight: SMALL_MARGIN,
        marginBottom: 0,
      },
    },
  }),
);

const Confirm: FC<IConfirmModal> = ({
  closeModal,
  onSubmit,
  title,
  description,
  cancelBtnCaption,
  submitBtnCaption,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Wrapper>
      <StyledCloseIcon onClick={closeModal} />
      <Typography variant="h3">{title}</Typography>
      {description && <Description>{description}</Description>}
      <Buttons>
        <CustomButton
          data-testid="cancelConfirm"
          btnClass={cn(classes.cancelBtn, 'cancel-btn')}
          onClick={closeModal}
          color="secondary"
        >
          {cancelBtnCaption || t('button.cancel')}
        </CustomButton>
        <CustomButton
          data-testid="submitConfirm"
          btnClass="confirm-btn"
          onClick={() => {
            onSubmit();
            closeModal();
          }}
        >
          {submitBtnCaption || t('button.delete')}
        </CustomButton>
      </Buttons>
    </Wrapper>
  );
};

export default Confirm;

const Buttons = styled('div')`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    flex-direction: row;
    justify-content: center;
  }
`;
const Description = styled(Typography)`
  color: ${colorsPalette.gray};
  font-size: 20px;
  margin-top: ${MIDDLE_MARGIN_PX};
  margin-bottom: ${MIDDLE_MARGIN_PX};
`;
const Wrapper = styled('div')`
  position: relative;
  background: ${({ theme }) => theme.palette.secondary.main};
  border-radius: 5px;
  max-width: 590px;
  padding: ${MIDDLE_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX};
  text-align: center;
`;
const StyledCloseIcon = styled(CloseIcon)`
  display: block;
  cursor: pointer;
  height: 14px;
  margin-bottom: ${SMALL_MARGIN_PX};
  margin-left: auto;
  width: 14px;
`;
